import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription, of, timer } from 'rxjs';
import { catchError, map, switchMap, takeWhile } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ConfirmationService } from '../../../vass-core/components/modal/confirmation.service';
import { CommonService } from '../../../vass-core/services/common.service';
import { CustomHttpParams } from '../../../vass-core/services/custom-http-params.class';
import { IS_PUBLIC_API } from '../../../vass-core/utils/is-public-api- context';
import { LogoutTimeoutModalComponent } from '../../components/logout-timeout-modal/logout-timeout-modal.component';
import { SecureLoginDataModel } from '../../models/crypto/crypto.models';
import { RegisterModel } from '../../models/register.model';
import { SESSIONSTORAGE_TOKEN } from '../../utils/constants';
import { CryptoService } from '../crypto/crypto.service';
import { NavigationService } from '../navigation/navigation.service';
import { UserDataService } from '../user-data/user-data.service';

@Injectable()
export class AuthService {
  private authenticated: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private token: BehaviorSubject<string> = new BehaviorSubject(undefined);
  private second_holder: BehaviorSubject<boolean> = new BehaviorSubject(undefined);
  private status: BehaviorSubject<string> = new BehaviorSubject('0');
  private updateStatusSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private rejectedMortgage: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private onOpenModalLogin: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private rejectMortgageReasons: BehaviorSubject<Array<string>> = new BehaviorSubject([]);
  private timeoutLogoutSubscription: Subscription;
  private timeoutLogoutRestarted: boolean;

  constructor(
    private commonService: CommonService,
    private navigationService: NavigationService,
    private _userDataService: UserDataService,
    private confirmationService: ConfirmationService,
    private cryptoService: CryptoService,
    private http: HttpClient,
  ) {
    this.setAuthenticated(this.hasSessionToken());
  }

  private hasSessionToken() {
    return sessionStorage.getItem(SESSIONSTORAGE_TOKEN) ? true : false;
  }

  public registerCase(body: RegisterModel) {
    const url = `${environment.apiBase}/mortgages/${environment.api_version.mortgages_simulator}/mortgage/register`;
    return this.http.post<{ id: string }>(url, body, {
      params: new CustomHttpParams(true),
      context: new HttpContext().set(IS_PUBLIC_API, true),
    });
  }

  public login(body: any, loginSecondHolder: boolean) {
    const url = loginSecondHolder
      ? `${environment.apiBase}/mortgages/${environment.api_version.mortgages_simulator}/mortgage/holder/secondary/signIn`
      : `${environment.apiBase}/mortgages/${environment.api_version.mortgages_simulator}/mortgage/signIn`;
    return this.http
      .post<{ id: string }>(url, body, {
        params: new CustomHttpParams(true),
        context: new HttpContext().set(IS_PUBLIC_API, true),
      })
      .pipe(
        map((response) => {
          this.logoutTimeout();
          return response;
        }),
      );
  }

  public validateOTP(body, loginSecondHolder: boolean) {
    return this.cryptoService
      .getSecureLogin(body.id, body.otp, new Date().getTime())
      .pipe(
        switchMap((secureLoginData) =>
          this.doLoginRequest(secureLoginData, loginSecondHolder),
        ),
      );
  }

  public revokeToken() {
    const url = `${environment.apiRevoke}`;
    const body = new HttpParams().set(`token`, sessionStorage.getItem('token'));
    const basic = sessionStorage.getItem('second-holder')
      ? `${environment.api_key_second_holder}`
      : `${environment.api_key}`;

    const headers = new HttpHeaders()
      .set('Content-Type', 'application/x-www-form-urlencoded')
      .set('Authorization', `Basic ${basic}`);

    return this.http.post(url, body.toString(), { headers }).pipe(
      map((res: HttpResponse<Object>) => res.ok),
      catchError((err: any) => of(false)),
    );
  }

  public logout() {
    this.setAuthenticated(false);
    this.setToken('');
    this._userDataService.clear();
    this.setStatus('0');
    sessionStorage.clear();
    this.navigationService.redirectToPreSimulator();
    this.setRejectedMortgage(false);
  }

  public logoutTimeout() {
    if (this.timeoutLogoutSubscription) {
      this.timeoutLogoutSubscription.unsubscribe();
    }
    this.timeoutLogoutSubscription = timer(0, environment.logout_timeout)
      .pipe(takeWhile(() => this.isAuthenticated()))
      .subscribe(() => {
        if (this.timeoutLogoutRestarted) {
          this.logout();
          this.openTimeoutLogoutModal();
          return;
        }
        this.timeoutLogoutRestarted = true;
      });
  }

  public reloadTimeoutLogout() {
    this.timeoutLogoutRestarted = false;
    this.logoutTimeout();
  }

  public openTimeoutLogoutModal(
    isHttpError = false,
    headerText = 'La sesión ha expirado',
  ) {
    if (this.isAuthenticated()) {
      const options: Object = {
        txtButton: '',
        showHeader: true,
        header: headerText,
        closeOnEscape: false,
        closable: true,
        styleClass: 'modal-class',
        cancelButton: false,
        acceptButton: false,
      };

      const data = {
        isHttpError,
      };

      this.confirmationService.confirm(<any>{
        component: LogoutTimeoutModalComponent,
        options,
        data,
      });
    }
  }

  public isAuthenticated(): boolean {
    return this.authenticated.getValue();
  }

  public setAuthenticated(authStatus) {
    this.authenticated.next(authStatus);
  }

  public setToken(token) {
    sessionStorage.setItem(SESSIONSTORAGE_TOKEN, token);
    this.token.next(token);
  }

  public getToken(): string {
    return sessionStorage.getItem(SESSIONSTORAGE_TOKEN);
  }

  public setSeconHolder(hasSeconHolder: boolean) {
    this.second_holder.next(hasSeconHolder);
  }

  public getSeconHolder(): boolean {
    return this.second_holder.getValue();
  }

  public getStatus() {
    return this.status;
  }

  public setStatus(status) {
    this.status.next(status);
  }

  public getUpdateStatus() {
    return this.updateStatusSubject;
  }

  public updateStatus(update) {
    this.updateStatusSubject.next(update);
  }

  private getServerTimestamp() {
    const url = `${environment.apiBase}/toolbox/${environment.api_version.toolbox}/timestamp/now`;
    return this.commonService.getPlainTextVerb(url, true);
  }

  private doLoginRequest(
    secureLoginData: SecureLoginDataModel,
    loginSecondHolder: boolean,
  ) {
    const url = loginSecondHolder
      ? `${environment.apiBase}/mortgage/auth/${environment.api_version.auth}/token/holder/secondary`
      : `${environment.apiBase}/mortgage/auth/${environment.api_version.auth}/token`;

    let headers = new HttpHeaders();
    headers = headers
      .set('KeyToken2', secureLoginData.keyToken2)
      .set('Content-type', 'application/json');

    const body = secureLoginData.body;

    return this.http
      .post(url, body, {
        headers,
        params: new CustomHttpParams(true),
        context: new HttpContext().set(IS_PUBLIC_API, true),
        observe: 'response',
      })
      .pipe(
        map((res: HttpResponse<any>) => res.body),
        map((res: any) => {
          if (res.access_token) {
            this.setToken(res.access_token);
            this.setAuthenticated(res.access_token ? true : false);
          }
        }),
      );
  }

  getRejectedMortgage() {
    return this.rejectedMortgage;
  }

  setRejectedMortgage(rejected: boolean) {
    this.rejectedMortgage.next(rejected);
  }

  getRejectMortgageReasons() {
    return this.rejectMortgageReasons;
  }

  setRejectMortgageReasons(reasons: Array<string>) {
    this.rejectMortgageReasons.next(reasons);
  }

  public openModalLogin() {
    this.onOpenModalLogin.next(true);
  }

  public changesOnOpenmodalLogin() {
    return this.onOpenModalLogin;
  }
}
